import { apiService } from './ApiService';

// ---- Response types (mirror catax_ai/routes/usage.py) --------------------- //

export type UsageMetric = 'orders' | 'part_queries' | 'llm_queries' | 'quotes' | 'images' | 'pdfs';
export type UsageStatus = 'within' | 'near' | 'reached' | 'unlimited';

export interface UsageMetricEntry {
  used: number;
  limit: number | null;
  pct?: number;
  remaining?: number;
  status: UsageStatus;
  projected_eom?: number;
  paused_until?: string;
}

// The org's quote-billing agreement terms + this month's consumption.
// Money is integer cents; absent (null) when no agreement is configured.
export interface AgreementBlock {
  plan_name?: string | null;
  currency: string;
  monthly_price_cents: number;
  included_quotes: number;
  overage_price_cents: number;
  allowances: { part_queries: number; images: number; pdfs: number };
  quotes_used: number;
  overage_quotes: number;
  overage_cost_cents: number;
  projected_eom_quotes?: number;
  projected_overage_cost_cents?: number;
}

export interface UsageSummary {
  org_id: number;
  plan: {
    id: string | null;
    name: string | null;
    limits: Record<UsageMetric, number | null>;
  };
  agreement: AgreementBlock | null;
  period: {
    month: string; // YYYY-MM
    start: string;
    end: string;
    resets_at: string;
    is_current: boolean;
    days_elapsed: number;
    days_in_month: number;
  };
  enforcement: 'off' | 'warn' | 'enforce';
  metrics: Record<UsageMetric, UsageMetricEntry>;
  available_months: string[];
}

export interface UsageDailyPoint {
  day: number;
  count: number;
}

export interface UsageDaily {
  month: string;
  previous_month: string;
  series: Record<UsageMetric, { current: UsageDailyPoint[]; previous: UsageDailyPoint[] }>;
}


// ── Per-advisor usage (org admins) ──────────────────────────────────────────
export type MemberKind = 'person' | 'support' | 'system';

export interface MemberUsage {
  key: string;                 // a user uid, or 'channel' | 'guest' | 'api'
  name: string;
  email: string | null;
  kind: MemberKind;
  role: string;
  disabled: boolean;
  quotes: number;
  searches: number;
  images: number;
  pdfs: number;
  orders: number;
  parts_found_pct: number | null;
  last_active: string | null;
}

export interface MembersUsage {
  month: string;
  include_disabled: boolean;
  members: MemberUsage[];
  totals: {
    quotes: number; searches: number; images: number; pdfs: number; orders: number;
    parts_found_pct: number | null; active_members: number; enabled_members: number;
  };
}

export interface MonthlyQuotes {
  months: string[];
  include_disabled: boolean;
  members: Array<Pick<MemberUsage, 'key' | 'name' | 'kind' | 'role' | 'disabled'> & { quotes: number[]; total: number }>;
  totals: number[];
  grand_total: number;
}

export type StageGroup = 'quoted' | 'in_progress' | 'needs_answer' | 'closed';

export interface MemberDetail {
  month: string;
  member: Pick<MemberUsage, 'key' | 'name' | 'email' | 'kind' | 'role' | 'disabled'>;
  totals: {
    quotes: number; searches: number; images: number; pdfs: number; orders: number;
    parts_found_pct: number | null; active_days: number; last_active: string | null;
  };
  daily: { quotes: number[]; searches: number[] };
  stages: Record<StageGroup, number>;
  orders: Array<{
    id: number;
    external_order_id: string | null;
    session_id: string | null;
    vehicle: string | null;
    vin: string | null;
    stage_group: StageGroup;
    stage: string | null;
    parts_requested: number;
    parts_found: number;
    searches: number;
    pdf: boolean;
    created_at: string;
  }>;
}

class UsageService {
  // The SPA catch-all serves index.html (HTTP 200) for unknown API paths — e.g.
  // a backend that predates these routes — so validate the payload shape
  // instead of letting HTML flow into the page as a "summary".
  private expectObject<T>(data: any, requiredKey: string): T {
    if (!data || typeof data !== 'object' || !(requiredKey in data)) {
      throw new Error('Unexpected /api/usage response — is the backend up to date?');
    }
    return data as T;
  }

  async getSummary(month?: string): Promise<UsageSummary> {
    const response = await apiService.get('/api/usage/summary', {
      params: month ? { month } : {},
    });
    return this.expectObject<UsageSummary>(response.data, 'period');
  }

  /** Paused metrics only — available to every org member (the summary is admin-only). */
  async getStatus(): Promise<Pick<UsageSummary, 'metrics'> & { plan?: { id: string | null } }> {
    const response = await apiService.get('/api/usage/status');
    return this.expectObject<any>(response.data, 'metrics');
  }

  async getDaily(month?: string): Promise<UsageDaily> {
    const response = await apiService.get('/api/usage/daily', {
      params: month ? { month } : {},
    });
    return this.expectObject<UsageDaily>(response.data, 'series');
  }

  async downloadReport(month: string): Promise<void> {
    const response = await apiService.get('/api/usage/report', {
      params: { month },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `usage-report-${month}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getMembers(month?: string, includeDisabled = false): Promise<MembersUsage> {
    const response = await apiService.get('/api/usage/members', {
      params: { ...(month ? { month } : {}), include_disabled: includeDisabled },
    });
    return this.expectObject<MembersUsage>(response.data, 'members');
  }

  async getMonthlyQuotes(endMonth?: string, months = 6, includeDisabled = false): Promise<MonthlyQuotes> {
    const response = await apiService.get('/api/usage/members/monthly', {
      params: { ...(endMonth ? { end_month: endMonth } : {}), months, include_disabled: includeDisabled },
    });
    return this.expectObject<MonthlyQuotes>(response.data, 'months');
  }

  async getMemberDetail(key: string, month?: string): Promise<MemberDetail> {
    const response = await apiService.get(`/api/usage/members/${encodeURIComponent(key)}`, {
      params: month ? { month } : {},
    });
    return this.expectObject<MemberDetail>(response.data, 'member');
  }

  async requestUpgrade(): Promise<void> {
    await apiService.post('/api/usage/upgrade-request');
  }
}

export const usageService = new UsageService();
